import React, { useRef, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch } from 'hooks/redux';
import {
  emailBuilderEnterBlockSettingsMode,
} from 'redux/actions/emailBuilder/emailBuilder';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Copy, ObjectUngroup, Trash2 } from '../emailBuilderIcons';
import { useConfirm } from '../shell/EmailBuilderConfirmProvider';
import { EMAIL_BUILDER_ACTION_RAIL_ATTR } from './actionRailConstants';
import {
  emailBuilderCanvasBlockRailActionTooltip,
  emailBuilderCanvasBlockReorderRailTooltip,
  emailBuilderLayoutColumnRailActionTooltip,
} from '../editing/emailBuilderCanvasBlockInteractions';
import { deleteSelectionWithConfirm } from '../lib/deleteSelection';
import { ungroupSelectionWithConfirm } from '../lib/ungroupSelection';
import { emailBuilderRowGroupRailActionTooltip } from '../shell/emailBuilderRowGroupHotkeys';
import { railActionIcon } from '../editing/emailBuilderCanvasBlockRail';
import { getSelectionCapabilities } from '../domain/selection/policy';
import { selectDocument, selectSelection } from '../domain/selectors';
import { useEditorActions, useEditorStore } from '../domain/store';
import type { Selection } from '../domain/types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useStableScopedDomId } from '../useStableDomId';
import { useEmailBuilderVerticalActionRailPin } from './useEmailBuilderVerticalActionRailPin';
import {
  emailBuilderVerticalActionRailClassName,
  emailBuilderVerticalActionRailPinStyle,
} from './emailBuilderVerticalActionRailChrome';

function isMacPlatform(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
  );
}

function moveBackTooltip(
  selection: Selection,
  label: string,
  isMac: boolean,
): string {
  if (selection.kind === 'block') {
    return emailBuilderCanvasBlockReorderRailTooltip('move-earlier', label, isMac);
  }
  if (selection.kind === 'column') {
    return emailBuilderLayoutColumnRailActionTooltip('move-left', label, isMac);
  }
  return label;
}

function moveForwardTooltip(
  selection: Selection,
  label: string,
  isMac: boolean,
): string {
  if (selection.kind === 'block') {
    return emailBuilderCanvasBlockReorderRailTooltip('move-later', label, isMac);
  }
  if (selection.kind === 'column') {
    return emailBuilderLayoutColumnRailActionTooltip('move-right', label, isMac);
  }
  return label;
}

type ActionRailButtonProps = {
  action: string;
  tooltip: string;
  ariaLabel: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
};

function ActionRailButton({
  action,
  tooltip,
  ariaLabel,
  disabled = false,
  danger = false,
  onClick,
  children,
}: ActionRailButtonProps) {
  const tooltipId = useStableScopedDomId('email-builder-action-rail', action);

  const button = (
    <button
      type="button"
      className={"email-builder-canvas-rail-btn"}
      data-danger={danger ? 'true' : undefined}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}>
      {children}
    </button>
  );

  return (
    <OverlayTrigger
      placement="right"
      flip
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
      {disabled ? <span className={"email-builder-canvas-rail-tooltip-target"}>{button}</span> : button}
    </OverlayTrigger>
  );
}

type ActionRailProps = {
  layout?: 'vertical' | 'inline';
  frameRef?: RefObject<HTMLElement | null>;
  useRowGroupAnchor?: boolean;
  onClaimHover?: (event: ReactMouseEvent<HTMLElement>) => void;
};

export function ActionRail({
  layout = 'vertical',
  frameRef,
  useRowGroupAnchor = false,
  onClaimHover,
}: ActionRailProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const document = useEditorStore(selectDocument);
  const selection = useEditorStore(selectSelection);
  const { duplicateSelected, deleteSelected, deleteGroup, moveSelected, ungroup } =
    useEditorActions();
  const { confirm } = useConfirm();
  const railRef = useRef<HTMLDivElement>(null);
  const portaled = layout === 'vertical' && frameRef !== undefined;
  const pinStyle = useEmailBuilderVerticalActionRailPin({
    enabled: portaled,
    frameRef,
    railRef,
    useRowGroupAnchor,
  });

  const capabilities = getSelectionCapabilities(document, selection);
  const showMoveButtons = capabilities?.showMoveButtons ?? false;

  if (!capabilities) {
    return null;
  }

  const { canUp, canDown, canDuplicate, canDelete, canUngroup, upLabel, downLabel } =
    capabilities;
  const moveHorizontal = selection.kind === 'column';
  const MoveBackIcon = moveHorizontal ? ChevronLeft : ChevronUp;
  const MoveForwardIcon = moveHorizontal ? ChevronRight : ChevronDown;
  const isMac = isMacPlatform();
  const duplicateTooltip = emailBuilderCanvasBlockRailActionTooltip(
    'duplicate',
    t('Duplicate'),
    isMac,
  );
  const deleteTooltip =
    !canDelete && (selection.kind === 'row' || selection.kind === 'group')
      ? t("Can't delete the last row")
      : emailBuilderCanvasBlockRailActionTooltip(
          'delete',
          t('Delete'),
          isMac,
        );
  const settingsTooltip = emailBuilderCanvasBlockRailActionTooltip(
    'settings',
    t('Settings'),
    isMac,
  );
  const ungroupTooltip = emailBuilderRowGroupRailActionTooltip(
    'ungroup',
    t('Ungroup'),
    isMac,
  );

  function handleDelete() {
    void deleteSelectionWithConfirm(
      document,
      selection,
      { confirm },
      { deleteSelected, deleteGroup },
      t,
    );
  }

  function handleUngroup() {
    void ungroupSelectionWithConfirm(
      document,
      selection,
      { confirm },
      { ungroup },
      t,
    );
  }

  const railClassName =
    layout === 'inline'
      ? "email-builder-canvas-rail-inline-group"
      : emailBuilderVerticalActionRailClassName(portaled);

  const verticalRailStyle =
    layout === 'inline' ? undefined : emailBuilderVerticalActionRailPinStyle(portaled, pinStyle);

  return (
    <div
      ref={railRef}
      className={railClassName}
      data-editor-chrome
      {...{ [EMAIL_BUILDER_ACTION_RAIL_ATTR]: '' }}
      style={verticalRailStyle}
      onMouseOver={onClaimHover}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}>
      {showMoveButtons ? (
        <>
          <ActionRailButton
            action="move-back"
            tooltip={moveBackTooltip(selection, t(upLabel), isMac)}
            ariaLabel={moveBackTooltip(selection, t(upLabel), isMac)}
            disabled={!canUp}
            onClick={() => moveSelected(-1)}>
            <MoveBackIcon size={16} aria-hidden />
          </ActionRailButton>
          <ActionRailButton
            action="move-forward"
            tooltip={moveForwardTooltip(selection, t(downLabel), isMac)}
            ariaLabel={moveForwardTooltip(selection, t(downLabel), isMac)}
            disabled={!canDown}
            onClick={() => moveSelected(1)}>
            <MoveForwardIcon size={16} aria-hidden />
          </ActionRailButton>
        </>
      ) : null}
      {layout === 'inline' && selection.kind !== 'email' ? (
        <ActionRailButton
          action="settings"
          tooltip={settingsTooltip}
          ariaLabel={t('Settings')}
          onClick={() => dispatch(emailBuilderEnterBlockSettingsMode())}>
          <FontAwesomeIcon icon={railActionIcon('settings')} aria-hidden />
        </ActionRailButton>
      ) : null}
      <ActionRailButton
        action="duplicate"
        tooltip={duplicateTooltip}
        ariaLabel={duplicateTooltip}
        disabled={!canDuplicate}
        onClick={duplicateSelected}>
        <Copy size={16} aria-hidden />
      </ActionRailButton>
      {selection.kind === 'group' ? (
        <ActionRailButton
          action="ungroup"
          tooltip={ungroupTooltip}
          ariaLabel={ungroupTooltip}
          disabled={!canUngroup}
          onClick={handleUngroup}>
          <ObjectUngroup size={16} aria-hidden />
        </ActionRailButton>
      ) : null}
      <ActionRailButton
        action="delete"
        tooltip={deleteTooltip}
        ariaLabel={deleteTooltip}
        danger
        disabled={!canDelete}
        onClick={handleDelete}>
        <Trash2 size={16} aria-hidden />
      </ActionRailButton>
    </div>
  );
}
